import React, { FunctionComponent, useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { LabelOutputType, PossibleLabelValue } from "../../context/ConfigContext/types";
import CheckIcon from "@mui/icons-material/Check";
import UndoIcon from "@mui/icons-material/Undo";
import ClearIcon from "@mui/icons-material/Clear";
import { AdminPanelLabel } from "./types";
import { useAdminPanelContext } from "../../context/AdminPanelContext";
import "./LabelValueCard.css";
import { confirmAlert } from "react-confirm-alert";

export enum ValueCardType {
  ADDED = "Added",
  EDITED = "Edited",
  UNTOUCHED = "Untouched",
  RELEVANCY = "Relevancy",
}

interface LabelValueCardProps {
  label: AdminPanelLabel;
  labelValue: PossibleLabelValue;
}

enum LabelValueAction {
  SAVE = "Save",
  DELETE = "Delete",
  UPDATE_ACTIVE = "Update active flag on",
}

const LabelValueCard: FunctionComponent<LabelValueCardProps> = ({ label, labelValue }) => {
  const { handleLabelValues } = useAdminPanelContext();
  const [value, setValue] = useState({ ...labelValue }.value || "");
  const [loading, setLoading] = useState(false);
  const [cardState, setCardState] = useState<ValueCardType>(null);

  useEffect(() => {
    if (labelValue.id < 0) return setCardState(ValueCardType.ADDED);
    if (label.outputType === LabelOutputType.RELEVANCY) return setCardState(ValueCardType.RELEVANCY);
    return setCardState(ValueCardType.UNTOUCHED);
  }, [labelValue]);

  const handleCardStateChange = (state: ValueCardType = null) => {
    if (cardState === ValueCardType.RELEVANCY) return;
    if (state) return setCardState(state);
    if (labelValue.id > 0) {
      setCardState(ValueCardType.EDITED);
    } else {
      setCardState(ValueCardType.UNTOUCHED);
    }
    setValue(labelValue.value);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
  };

  const handleSave = async () => {
    if (value === labelValue.value) return;
    if (cardState === ValueCardType.ADDED) {
      setLoading(true);
      await handleLabelValues.add(label.id, { id: labelValue.id, value: value });
      setLoading(false);
    }
    if (cardState === ValueCardType.EDITED) {
      setLoading(true);
      await handleLabelValues.update(label.id, { id: labelValue.id, value: value });
      setLoading(false);
      handleCardStateChange(ValueCardType.UNTOUCHED);
    }
    setValue(labelValue.value);
  };

  const handleUndo = () => {
    handleCardStateChange(ValueCardType.UNTOUCHED);
    setValue(labelValue.value);
  };

  const handleDelete = () => {
    if (labelValue.id > 0) return;
    handleLabelValues.delete(label.id, labelValue.id);
  };

  const checkDuplicateName = () => {
    return label.possibleLabelValues.some(
      (otherLabelValue) => otherLabelValue.id !== labelValue.id && otherLabelValue.value === value
    );
  };

  const handleUpdateActive = () => {
    handleLabelValues.update(label.id, { ...labelValue, active: !labelValue.active }, true);
  };

  const submit = (action: LabelValueAction) => {
    const confirmAlertObj = {
      title: `${action} label value`,
      message: `Are you sure you want to ${action} label value id ${labelValue.id}`,
      buttons: [
        {
          label: "Confirm",
          onClick: () => null,
        },
        {
          label: "Cancel",
        },
      ],
    };
    if (action === LabelValueAction.UPDATE_ACTIVE) confirmAlertObj.buttons[0].onClick = handleUpdateActive;
    confirmAlert(confirmAlertObj);
  };

  return (
    <>
      {cardState === ValueCardType.ADDED ? (
        <Box sx={{ height: "100%" }}>
          <TextField
            className="Unsaved-text-field"
            error={!!value && checkDuplicateName()}
            sx={{ height: "100%" }}
            variant="outlined"
            color="primary"
            value={value}
            onChange={handleChange}
            autoFocus
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton color="error" disabled={loading} size="small" onClick={handleDelete}>
                    <ClearIcon />
                  </IconButton>
                  <IconButton
                    color="success"
                    disabled={!value || loading || checkDuplicateName()}
                    size="small"
                    onClick={handleSave}
                  >
                    <CheckIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      ) : (
        <Tooltip
          arrow
          title={
            <Typography variant="caption">
              Possible label value:
              <br />
              Id: {labelValue.id}
              <br />
              Value: {labelValue.value}
            </Typography>
          }
        >
          <Box sx={{ height: "100%" }}>
            {cardState === ValueCardType.EDITED ? (
              <TextField
                className="Unsaved-text-field"
                sx={{ height: "100%" }}
                error={!!value && checkDuplicateName()}
                variant="outlined"
                color="primary"
                value={value}
                onChange={handleChange}
                autoFocus
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton disabled={loading} size="small" onClick={handleUndo}>
                        <UndoIcon />
                      </IconButton>
                      <Switch
                        color="success"
                        size="small"
                        checked={labelValue.active}
                        onChange={() => submit(LabelValueAction.UPDATE_ACTIVE)}
                      />
                      {/* <IconButton color="error" disabled={loading || true} size="small" onClick={handleDelete}>
                        <ClearIcon />
                      </IconButton>
                      <IconButton
                        color="success"
                        disabled={!value || loading || checkDuplicateName() || true}
                        size="small"
                        onClick={handleSave}
                      >
                        <CheckIcon />
                      </IconButton> */}
                    </InputAdornment>
                  ),
                }}
              />
            ) : (
              <Card className="unclicked-card" variant="outlined" onClick={() => handleCardStateChange()}>
                <CardContent>
                  <Typography>{labelValue.value}</Typography>
                </CardContent>
              </Card>
            )}
          </Box>
        </Tooltip>
      )}
    </>
  );
};

export default LabelValueCard;
